import React, { useEffect, useRef, useState } from 'react';
import config from '../config';
import { useNavigate } from "react-router";

const VoiceCommandBar = () => {
  const recognitionRef = useRef(null);
  const triggerRecognitionRef = useRef(null);
  const buttonRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [voiceControlEnabled, setVoiceControlEnabled] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const triggerWord = 'aller sur la page';

  useEffect(() => {
    const htmlLang = document.documentElement.lang || 'fr-FR';
    window.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!window.SpeechRecognition) {
      setError("La reconnaissance vocale n'est pas supportée dans ce navigateur.");
      return;
    }

    // Reconnaissance standard pour navigation
    const recognition = new window.SpeechRecognition();
    recognition.lang = htmlLang;
    recognition.continuous = false;
    recognition.interimResults = false;

    recognitionRef.current = recognition;

    recognition.onresult = async (event) => {
      const speechResult = event.results[0][0].transcript.toLowerCase();
      console.log("🎤 Résultat vocal:", speechResult);
      setListening(false);

      const links = document.querySelectorAll('a, button');
      let found = false;

      links.forEach(link => {
        const text = link.textContent?.toLowerCase() || '';
        const title = link.getAttribute('title')?.toLowerCase() || '';
        const ariaLabel = link.getAttribute('aria-label')?.toLowerCase() || '';

        if (text.includes(speechResult) || title.includes(speechResult) || ariaLabel.includes(speechResult)) {
          found = true;
          link.click();

          const isLink = link.tagName.toLowerCase() === 'a';
          const spokenSource = ariaLabel || title || text || 'élément';

          const spokenText = isLink
            ? `Je vous redirige vers la page ${spokenSource}`
            : `Activation du bouton ${spokenSource}`;

          speak(spokenText);
        }
      });

      if (!found) {
        try {
          const response = await fetch(`${config.apiBaseUrl}/courses?titre=${encodeURIComponent(speechResult)}`);
          const courses = await response.json();

          if (courses.length > 0) {
            const course = courses[0];
            speak(`Redirection vers le cours ${course.titre}`);
            console.log(`➡️ Navigation vers /cours/${course.id_cours}`);
            navigate(`/cours/${course.id_cours}`);
          } else {
            // Recherche de leçon
            try {
              const lessonRes = await fetch(`${config.apiBaseUrl}/lessons?titre=${encodeURIComponent(speechResult)}`);
              const lessons = await lessonRes.json();

              if (lessons.length > 0) {
                const lesson = lessons[0];
                speak(`Redirection vers la leçon ${lesson.titre}`);
                console.log(`➡️ Navigation vers /cours/${lesson.id_cours}/lecon/${lesson.id_lesson}`);
                navigate(`/cours/${lesson.id_cours}/lecon/${lesson.id_lesson}`);
              } else {
                const notFoundText = `Aucun lien, cours, ni leçon trouvé pour "${speechResult}"`;
                alert(notFoundText);
                speak(notFoundText);
              }
            } catch (lessonErr) {
              console.error('Erreur lors de la recherche de leçon:', lessonErr);
              const errorText = 'Erreur lors de la recherche de leçon.';
              setError(errorText);
              speak(errorText);
            }
          }
        } catch (err) {
          console.error('Erreur lors de la recherche de cours:', err);
          const errorText = 'Erreur lors de la recherche de cours.';
          setError(errorText);
          speak(errorText);
        }
      }
    };

    recognition.onspeechend = () => {
      recognition.stop();
      setListening(false);
      if (voiceControlEnabled && triggerRecognitionRef.current) {
        triggerRecognitionRef.current.start();
      }
    };

    recognition.onend = () => {
      setListening(false);
      
    };
    

    recognition.onerror = (event) => {
      console.error("Erreur reconnaissance vocale:", event);
      setListening(false);
      const errText = 'Erreur de reconnaissance vocale : ' + event.error;
      setError(errText);
      speak(errText);
      if (voiceControlEnabled && triggerRecognitionRef.current) {
        triggerRecognitionRef.current.start();
      }
    };

    // Reconnaissance continue pour le mot-clé
    const triggerRecognition = new window.SpeechRecognition();
    triggerRecognition.lang = htmlLang;
    triggerRecognition.continuous = true;
    triggerRecognition.interimResults = false;

    triggerRecognition.onresult = (event) => {
      const result = event.results[event.results.length - 1][0].transcript.toLowerCase();
      console.log('🗣️ Mot entendu:', result);
      if (result.includes('arrêter contrôle vocal') || result.includes('contrôle vocal off')) {
        setVoiceControlEnabled(false);
        speak("Contrôle vocal désactivé");
        return;
      }
      
    
      if (result.includes(triggerWord)) {
        triggerRecognitionRef.current.stop();
        speak(null, () => {
          startRecognition();
        });
      }
      
    };

    triggerRecognition.onerror = (e) => {
      console.warn('Erreur écoute continue:', e);
    };

    triggerRecognitionRef.current = triggerRecognition;

    return () => {
      triggerRecognition.stop();
    };
  }, []);

  // Gère le mode contrôle vocal activé/désactivé
  useEffect(() => {
    if (voiceControlEnabled && triggerRecognitionRef.current) {
      triggerRecognitionRef.current.start();
    } else if (triggerRecognitionRef.current) {
      triggerRecognitionRef.current.stop();
    }
  }, [voiceControlEnabled]);

  const speak = (textOrNull, onEnd = null) => {
    if (!textOrNull) {
      const beep = new Audio('https://www.soundjay.com/buttons/beep-02.mp3');
      beep.play().then(() => {
        if (onEnd) onEnd();
      });
      return;
    }
  
    const utterance = new SpeechSynthesisUtterance(textOrNull);
    utterance.lang = document.documentElement.lang || 'fr-FR';
    if (onEnd) utterance.onend = onEnd;
    speechSynthesis.speak(utterance);
  };
  
  

  const startRecognition = () => {
    if (recognitionRef.current) {
      setListening(true);
      setError(null);
      recognitionRef.current.start();
    }
  };

  return (
    <nav className="voice-command-bar" aria-label="Navigation à la voix">
      <span className="vc-text">🎤 Commandes vocales :</span>
      <button
        ref={buttonRef}
        className={`vc-button ${listening ? 'listening' : ''}`}
        onClick={startRecognition}
      >
        {listening ? <span className="pulse-mic">🎧</span> : <>Aller sur la page</>}
      </button>
      <button
        className={`vc-button toggle ${voiceControlEnabled ? 'enabled' : ''}`}
        onClick={() => setVoiceControlEnabled(prev => !prev)}
        aria-pressed={voiceControlEnabled}
        aria-label="Activer ou désactiver le contrôle vocal"
      >
        {voiceControlEnabled ? '🔊 Contrôle vocal ON' : '🔇 Contrôle vocal OFF'}
      </button>
      {error && <div className="vc-error" role="alert">{error}</div>}
    </nav>
  );
};

export default VoiceCommandBar;
